import { Link } from 'react-router-dom';
import { polygonAreaKmSq } from '@soc/shared';
import { useDeleteProjectMutation, useGetOrganizationsQuery, useGetProjectsQuery } from '../app/api';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import { selectProject } from '../features/projects/projectsSlice';
import { useAuth } from '../features/auth/useAuth';
import { AdminScreen, ListCount, card, pillButton, primaryButtonStyle } from '../features/admin/adminUi';
import { formatAreaKmSq } from '../features/admin/formUi';

export default function Projects() {
  const dispatch = useAppDispatch();
  const { role } = useAuth();
  const isSuper = role === 'super_admin';
  const canManage = role === 'admin' || isSuper;

  const selectedId = useAppSelector((s) => s.projects.selectedId);
  const { data } = useGetProjectsQuery();
  const { data: orgs } = useGetOrganizationsQuery(undefined, { skip: !isSuper });
  const [deleteProject] = useDeleteProjectMutation();

  const orgName = (id: string | null) => (id ? orgs?.items.find((o) => o.id === id)?.name ?? '—' : '—');

  return (
    <AdminScreen
      title="PROJECTS"
      subtitle="Monitored areas — detection & queries are scoped to a project polygon"
    >
      <div className="scrl" style={{ overflowY: 'auto', padding: 24, minHeight: 0 }}>
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <ListCount n={data?.total ?? 0} noun="project" />
            {canManage && (
              <Link to="/projects/new" style={{ ...primaryButtonStyle(true), textDecoration: 'none' }}>
                + New project
              </Link>
            )}
          </div>
          {data?.items.map((p) => {
            const active = p.id === selectedId;
            return (
              <div
                key={p.id}
                style={{
                  ...card,
                  marginBottom: 8,
                  border: `1px solid ${active ? 'rgba(0,229,255,0.4)' : 'var(--border)'}`,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 14, fontWeight: 600 }}>{p.name}</span>
                  <span className="mono" style={{ fontSize: 10, color: 'var(--text-dim)' }}>
                    {formatAreaKmSq(polygonAreaKmSq(p.polygon))}
                  </span>
                </div>
                {isSuper && (
                  <div style={{ fontSize: 11, color: 'var(--text-faint)', marginTop: 4 }}>{orgName(p.orgId)}</div>
                )}
                <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
                  <button
                    type="button"
                    onClick={() => dispatch(selectProject(active ? null : p.id))}
                    style={{
                      ...pillButton,
                      color: active ? 'var(--ok)' : 'var(--accent)',
                      borderColor: active ? 'rgba(41,224,168,0.4)' : 'var(--border)',
                    }}
                  >
                    {active ? '✓ Selected' : 'Select'}
                  </button>
                  {canManage && (
                    <>
                      <Link to={`/projects/${p.id}/edit`} style={{ ...pillButton, textDecoration: 'none' }}>
                        Edit
                      </Link>
                      <button
                        type="button"
                        onClick={async () => {
                          await deleteProject(p.id).unwrap().catch(() => undefined);
                          if (active) dispatch(selectProject(null));
                        }}
                        style={pillButton}
                      >
                        Delete
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </AdminScreen>
  );
}
